import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase, logAiRequest } from '../supabaseClient';
import { ShareButton } from './ShareButton';
import { Loader2, Lightbulb, Star, CalendarDays } from 'lucide-react';
import { useDailyAiContent, getOsloDateKey } from '../hooks/useDailyAiContent';
import { VOICE_GUIDE, COUNTRIES, CITIES, FACT_CATEGORIES, pickRandom } from '../world-core';

interface FactContent {
    no: { text: string };
    en: { text: string };
    votes?: number;
    score?: number;
}

// Shared "daily_content" rows are cached once and read by every visitor
// regardless of their chosen language, so the AI is asked for both
// versions of the same fact in one response instead of generating (and
// caching) a separate fact per language.
function parseBilingualFact(raw: string): { no: string; en: string } | null {
    const noMatch = raw.match(/NO:\s*([\s\S]*?)(?:\n\s*EN:|$)/i);
    const enMatch = raw.match(/EN:\s*([\s\S]*)$/i);
    const no = noMatch?.[1]?.trim();
    const en = enMatch?.[1]?.trim();
    if (!no || !en) return null;
    return { no, en };
}

const isValidFact = (content: FactContent | null | undefined): boolean => {
    const no = content?.no?.text;
    const en = content?.en?.text;
    const isCleanText = (text: string) => !(text.includes('{"role"') || text.includes('tool_calls') || text.length > 400);
    return !!no && !!en && isCleanText(no) && isCleanText(en);
};

const generateFact = async (): Promise<FactContent> => {
    const category = pickRandom(FACT_CATEGORIES);
    const isCountry = Math.random() < 0.6;
    const subject = isCountry ? pickRandom(COUNTRIES) : pickRandom(CITIES);
    const temperature = 0.8 + Math.random() * 0.3;

    const prompt = `${VOICE_GUIDE}

Skriv ETT ENKELT ekte "Visste du at"-faktum, på norsk. Deretter oversett akkurat det samme faktumet naturlig til engelsk.

${isCountry ? 'Landet' : 'Byen'}: ${subject}. Kategori: "${category}".

REGLER:
1. Faktumet MÅ være ekte og korrekt - ikke dikt opp noe.
2. Skriv den norske versjonen på vanlig norsk. IKKE bruk vanskelig lokal-dialekt.
3. Det skal være overraskende og morsomt - men aldri på bekostning av at det stemmer.
4. IKKE gi meg en introduksjon eller hashtag, BARE selve faktumet. Maks 20-45 ord per språk. Start alltid med "Visste du at..." / "Did you know...".

Svar KUN nøyaktig i dette formatet, ingen annen tekst:
NO: <faktumet på norsk>
EN: <the same fact in natural English>`;

    const apiRes = await fetch('/api/gemini', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            model: 'gemini-2.5-flash',
            contents: { parts: [{ text: prompt }] },
            config: { temperature, tools: [{ googleSearch: {} }] }
        })
    });

    if (!apiRes.ok) {
        const errData = await apiRes.json().catch(() => ({}));
        throw new Error(errData.error || apiRes.statusText);
    }

    const response = await apiRes.json();
    const text = response.text;
    if (!text) throw new Error('Empty AI response');

    const parsed = parseBilingualFact(text);
    if (!parsed) throw new Error('Could not parse bilingual AI response');

    await logAiRequest('daily_fact', true, { category, subject });
    return { no: { text: parsed.no }, en: { text: parsed.en } };
};

const fallbackFact = (t: (key: string) => string): FactContent => {
    const keys = ['fallback1', 'fallback2', 'fallback3', 'fallback4', 'fallback5', 'fallback6'];
    const key = keys[Math.floor(Math.random() * keys.length)];
    return {
        no: { text: t(key) },
        en: { text: t(key) },
    };
};

export const DailyFact: React.FC = () => {
    const { t, i18n } = useTranslation('DailyFact');
    const lang = i18n.language.startsWith('en') ? 'en' : 'no';

    const { data: content, loading } = useDailyAiContent<FactContent>({
        type: 'fact',
        generate: generateFact,
        fallback: () => fallbackFact(t),
        isValid: isValidFact,
    });
    const fact = content?.[lang]?.text ?? t('loadingWisdom');

    const [factImageUrl, setFactImageUrl] = useState<string | null>(null);
    const [rating, setRating] = useState<number>(0);
    const [hasRated, setHasRated] = useState<boolean>(false);
    const [dailyEmoji, setDailyEmoji] = useState<string>("💡");
    const [dayProgress, setDayProgress] = useState({ percent: 0, textKey: "" });

    const EMOJIS = ["💩", "🤡", "🚜", "🐠", "🍻", "🗑️", "🚽", "🐷", "🐸", "🧌"];

    useEffect(() => {
        const today = new Date();
        const dateStr = getOsloDateKey(today);

        // Pick deterministic emoji based on date
        const emojiIndex = (today.getDate() + today.getMonth()) % EMOJIS.length;
        setDailyEmoji(EMOJIS[emojiIndex]);

        // Check if user already rated today
        const savedRating = localStorage.getItem(`skjeta_fact_rating_${dateStr}`);
        if (savedRating) {
            setRating(parseInt(savedRating));
            setHasRated(true);
        }

        // Calculate 'Snart Helg' Skjeta-level progress
        const dayOfWeek = today.getDay(); // 0 = Sun, 1 = Mon, ... 6 = Sat
        let percent = 0;
        let textKey = "";

        switch (dayOfWeek) {
            case 1: percent = 10; textKey = "monday"; break;
            case 2: percent = 30; textKey = "tuesday"; break;
            case 3: percent = 50; textKey = "wednesday"; break;
            case 4: percent = 80; textKey = "thursday"; break;
            case 5: percent = 100; textKey = "friday"; break;
            case 6: percent = 100; textKey = "saturday"; break;
            case 0: percent = 10; textKey = "sunday"; break;
        }

        setDayProgress({ percent, textKey });
    }, []);

    const handleRate = async (stars: number) => {
        if (hasRated) return;

        const dateStr = getOsloDateKey();
        setRating(stars);
        setHasRated(true);
        localStorage.setItem(`skjeta_fact_rating_${dateStr}`, stars.toString());

        // We could write the rating to Supabase daily_content here if we wanted to
        // aggregate it for "Månedens Skjeta-faktum", but for now it's local.
        try {
            // Attempt to update the DB fact entry with a vote tally
            // This is a simplified approach, usually requires a proper RPC function
            const { data } = await supabase
                .from('daily_content')
                .select('content')
                .eq('date', dateStr)
                .eq('type', 'fact')
                .single();

            if (data && data.content) {
                const currentContent = data.content as FactContent;
                const currentVotes = currentContent.votes || 0;
                const currentScore = currentContent.score || 0;

                await supabase.from('daily_content').upsert({
                    date: dateStr,
                    type: 'fact',
                    content: {
                        ...currentContent,
                        votes: currentVotes + 1,
                        score: currentScore + stars
                    }
                });
            }
        } catch (e) {
            console.error("Failed to save rating", e);
        }
    };

    useEffect(() => {
        if (fact && fact !== t('loadingWisdom')) {
            const shortFact = fact.substring(0, 60).replace(/[^a-zA-Z0-9 ]/g, "");
            const seed = getOsloDateKey().replace(/-/g, "");
            const promptUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(`absurd weird illustration of: ${shortFact}`)}?width=400&height=300&seed=${seed}&nologo=true`;
            setFactImageUrl(promptUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fact]);

    return (
        <div className="bg-gradient-to-br from-zinc-900 to-black border border-skjeta-green/20 rounded-xl p-6 relative overflow-hidden group hover:border-skjeta-green/50 transition-all shadow-lg flex flex-col gap-6">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Lightbulb size={80} className="text-skjeta-green group-hover:animate-pulse" />
            </div>

            <div className="relative z-10">
                <h2 className="text-skjeta-green font-black uppercase text-sm tracking-widest flex items-center gap-2 mb-3">
                    <span className="text-xl">{dailyEmoji}</span> {t('heading')}
                </h2>

                {loading ? (
                    <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-sm italic">{t('diggingInTrash')}</span>
                    </div>
                ) : (
                    <div>
                        <div className="flex flex-col md:flex-row gap-6 mb-4 items-center">
                            <p className="text-white font-medium text-lg italic leading-relaxed flex-1">
                                "{fact}"
                            </p>
                            {factImageUrl && (
                                <img loading="lazy"
                                    src={factImageUrl}
                                    alt={t('dailyImageAlt')}
                                    className="w-full md:w-32 h-auto rounded-lg shadow-lg border border-white/10 shrink-0"
                                />
                            )}
                        </div>

                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider mr-2">{t('validateFactor')}</span>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => handleRate(star)}
                                    disabled={hasRated}
                                    className={`p-1 transition-all ${hasRated ? (star <= rating ? 'text-skjeta-green opacity-100' : 'text-gray-700 opacity-50') : 'text-gray-600 hover:text-skjeta-green hover:scale-110'}`}
                                >
                                    <Star size={18} fill={hasRated && star <= rating ? 'currentColor' : 'none'} className={hasRated && star <= rating ? 'drop-shadow-[0_0_8px_rgba(163,230,53,0.8)]' : ''} />
                                </button>
                            ))}

                            {hasRated && <span className="ml-2 text-xs text-skjeta-green italic">{t('thanksForVote')}</span>}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <ShareButton
                                title={t('heading')}
                                text={fact}
                                url="https://skjeta.no"
                                className="bg-white/5 px-4 py-2 rounded-full hover:bg-white/10"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="relative z-10 bg-black/40 rounded-lg p-4 border border-white/5">
                <div className="flex items-center justify-between mb-4 text-xs font-bold uppercase tracking-widest text-gray-400">
                    <span className="flex items-center gap-2">
                        <CalendarDays size={14} className="text-skjeta-green" />
                        {t('weekendMeter')}
                    </span>
                    <span className="text-skjeta-green">{t('percentSkjeta', { percent: dayProgress.percent })}</span>
                </div>

                <div className="w-full bg-zinc-800 rounded-full h-4 mb-2 overflow-hidden border border-white/10">
                    <div
                        className="bg-gradient-to-r from-skjeta-purple to-skjeta-green h-4 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${dayProgress.percent}%` }}
                    />
                </div>
                <p className="text-xs text-gray-400 font-medium text-right italic">
                    {dayProgress.textKey && t(dayProgress.textKey)}
                </p>
            </div>
        </div>
    );
};
